import React from "react";
import { T } from '../shared/constants';

export default function LumiDiagram({ type: diagramType }) {
  if (diagramType === 'states') {
    const [state, setState] = React.useState('solid');
    const configs = {
      solid:  { particles: [[40,40],[80,40],[120,40],[40,80],[80,80],[120,80],[40,120],[80,120],[120,120]], color:'#3B82F6', motion:0, label:'Particles vibrate in fixed positions \u2014 very close together' },
      liquid: { particles: [[35,45],[75,35],[115,50],[45,85],[85,80],[125,75],[40,125],[85,120],[120,115]], color:'#10B981', motion:1, label:'Particles slide past each other \u2014 close but not fixed' },
      gas:    { particles: [[30,30],[100,25],[150,60],[20,90],[130,85],[60,130],[160,35],[80,110],[150,140]], color:'#F59E0B', motion:2, label:'Particles move freely and fast \u2014 spread far apart' },
    };
    const cfg = configs[state];
    const [tick, setTick] = React.useState(0);
    React.useEffect(() => {
      const id = setInterval(() => setTick(t => t+1), 120);
      return () => clearInterval(id);
    }, []);
    const jitter = (v, amount) => v + (Math.sin(tick * 0.7 + v) * amount);
    return (
      <div style={{background:T.card,borderRadius:14,padding:14,border:`1px solid ${T.border}`}}>
        <p style={{fontSize:11,fontWeight:800,color:cfg.color,textTransform:'uppercase',letterSpacing:'.08em',margin:'0 0 10px'}}>States of Matter {"\u2014"} Particle Model</p>
        <div style={{display:'flex',gap:8,marginBottom:12,justifyContent:'center'}}>
          {['solid','liquid','gas'].map(s => (
            <button key={s} onClick={() => setState(s)} style={{padding:'5px 14px',borderRadius:99,border:`1.5px solid ${configs[s].color}`,background: state===s ? configs[s].color : 'transparent',color: state===s ? 'white' : configs[s].color,fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit',transition:'all .2s',textTransform:'capitalize'}}>{s}</button>
          ))}
        </div>
        <svg width="180" height="170" style={{display:'block',margin:'0 auto',background:`${cfg.color}08`,borderRadius:10,border:`1px solid ${cfg.color}33`}}>
          {cfg.particles.map((p, i) => (
            <circle key={i}
              cx={jitter(p[0], cfg.motion === 0 ? 1 : cfg.motion === 1 ? 4 : 12)}
              cy={jitter(p[1], cfg.motion === 0 ? 1 : cfg.motion === 1 ? 4 : 12)}
              r={cfg.motion === 2 ? 8 : 10}
              fill={cfg.color} fillOpacity="0.85" stroke="white" strokeWidth="1.5"
            />
          ))}
        </svg>
        <p style={{fontSize:11,color:T.textMid,textAlign:'center',margin:'10px 0 0',lineHeight:1.5}}>{cfg.label}</p>
      </div>
    );
  }

  if (diagramType === 'forces') {
    const [pushing, setPushing] = React.useState(60);
    const [friction, setFriction] = React.useState(20);
    const net = pushing - friction;
    const moving = net > 0;
    return (
      <div style={{background:T.card,borderRadius:14,padding:14,border:`1px solid ${T.border}`}}>
        <p style={{fontSize:11,fontWeight:800,color:'#EF4444',textTransform:'uppercase',letterSpacing:'.08em',margin:'0 0 12px'}}>Forces {"\u2014"} Push vs Friction</p>
        <svg width="260" height="120" style={{display:'block',margin:'0 auto'}}>
          <line x1="20" y1="95" x2="240" y2="95" stroke={T.border} strokeWidth="2"/>
          <rect x={moving ? 100 : 95} y="65" width="50" height="30" rx="6"
            fill={`${T.accent}33`} stroke={T.accent} strokeWidth="2"
            style={{transition:'x .3s'}}/>
          <text x="125" y="84" textAnchor="middle" fontSize="11" fill={T.text} fontWeight="700">{"\uD83D\uDCE6"}</text>
          <defs>
            <marker id="arrowR" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
              <path d="M0,0 L0,6 L8,3 z" fill="#10B981"/>
            </marker>
            <marker id="arrowL" markerWidth="8" markerHeight="8" refX="2" refY="3" orient="auto">
              <path d="M8,0 L8,6 L0,3 z" fill="#EF4444"/>
            </marker>
          </defs>
          <line x1="20" y1="80" x2={20 + pushing * 0.7} y2="80" stroke="#10B981" strokeWidth="3" markerEnd="url(#arrowR)"/>
          <text x="40" y="72" fontSize="10" fill="#10B981" fontWeight="700">Push {pushing}N {"\u2192"}</text>
          <line x1="240" y1="80" x2={240 - friction * 0.7} y2="80" stroke="#EF4444" strokeWidth="3" markerEnd="url(#arrowL)"/>
          <text x="200" y="72" fontSize="10" fill="#EF4444" fontWeight="700">{"\u2190"} Friction {friction}N</text>
          <text x="125" y="115" textAnchor="middle" fontSize="11" fill={net > 0 ? '#10B981' : net < 0 ? '#EF4444' : T.textMid} fontWeight="800">
            Net force: {net > 0 ? '+' : ''}{net}N {net > 0 ? '\u2192 Moving!' : net < 0 ? '\u2190 Moving back!' : '= Balanced'}
          </text>
        </svg>
        <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:10}}>
          <div><p style={{fontSize:10,color:'#10B981',margin:'0 0 2px',fontWeight:700}}>Push force: {pushing}N</p>
            <input type="range" min="0" max="100" value={pushing} onChange={e=>setPushing(+e.target.value)} style={{width:'100%',accentColor:'#10B981'}}/></div>
          <div><p style={{fontSize:10,color:'#EF4444',margin:'0 0 2px',fontWeight:700}}>Friction: {friction}N</p>
            <input type="range" min="0" max="100" value={friction} onChange={e=>setFriction(+e.target.value)} style={{width:'100%',accentColor:'#EF4444'}}/></div>
        </div>
      </div>
    );
  }

  if (diagramType === 'lifecycle') {
    const cycles = {
      butterfly: { stages: [{icon:'\uD83E\uDD5A',label:'Egg',desc:'Laid on a leaf'},{icon:'\uD83D\uDC1B',label:'Larva',desc:'Caterpillar eats constantly'},{icon:'\uD83E\uDED8',label:'Pupa',desc:'Chrysalis \u2014 transformation'},{icon:'\uD83E\uDD8B',label:'Adult',desc:'Emerges, mates, lays eggs'}], color:'#8B5CF6', name:'Butterfly (Complete Metamorphosis)' },
      frog:      { stages: [{icon:'\uD83D\uDFE2',label:'Egg',desc:'Laid in water'},{icon:'\uD83D\uDC1F',label:'Tadpole',desc:'Gills \u2014 lives in water'},{icon:'\uD83D\uDC38',label:'Froglet',desc:'Grows legs, loses tail'},{icon:'\uD83D\uDC38',label:'Adult Frog',desc:'Lungs \u2014 lives on land'}], color:'#10B981', name:'Frog (Complete Metamorphosis)' },
      plant:     { stages: [{icon:'\uD83C\uDF31',label:'Seed',desc:'Germinates with water'},{icon:'\uD83C\uDF3F',label:'Seedling',desc:'Grows roots and shoots'},{icon:'\uD83C\uDF38',label:'Flower',desc:'Pollination occurs'},{icon:'\uD83C\uDF4E',label:'Fruit/Seed',desc:'New seeds dispersed'}], color:'#F59E0B', name:'Flowering Plant' },
    };
    const [which, setWhich] = React.useState('butterfly');
    const cyc = cycles[which];
    return (
      <div style={{background:T.card,borderRadius:14,padding:14,border:`1px solid ${T.border}`}}>
        <p style={{fontSize:11,fontWeight:800,color:cyc.color,textTransform:'uppercase',letterSpacing:'.08em',margin:'0 0 10px'}}>Life Cycles</p>
        <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap'}}>
          {Object.keys(cycles).map(k => (
            <button key={k} onClick={() => setWhich(k)} style={{padding:'4px 10px',borderRadius:99,border:`1.5px solid ${cycles[k].color}`,background: which===k ? cycles[k].color : 'transparent',color: which===k ? 'white' : cycles[k].color,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit',textTransform:'capitalize'}}>{k}</button>
          ))}
        </div>
        <p style={{fontSize:11,color:T.textMid,margin:'0 0 12px',textAlign:'center',fontWeight:600}}>{cyc.name}</p>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          {cyc.stages.map((st, i) => (
            <div key={i} style={{background:`${cyc.color}10`,border:`1.5px solid ${cyc.color}33`,borderRadius:12,padding:'10px 12px',position:'relative'}}>
              <div style={{position:'absolute',top:-10,left:10,width:20,height:20,borderRadius:'50%',background:cyc.color,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800,color:'white'}}>{i+1}</div>
              <div style={{fontSize:24,textAlign:'center',marginBottom:4,marginTop:4}}>{st.icon}</div>
              <div style={{fontSize:12,fontWeight:700,color:T.text,textAlign:'center',marginBottom:2}}>{st.label}</div>
              <div style={{fontSize:10,color:T.textMid,textAlign:'center',lineHeight:1.4}}>{st.desc}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (diagramType === 'classification') {
    const [selected, setSelected] = React.useState(null);
    const tree = [
      { level:"Kingdom", name:"Animalia", color:"#EF4444", desc:"All animals \u2014 multicellular, consume food" },
      { level:"Phylum",  name:"Chordata", color:"#F97316", desc:"Has a backbone (vertebrate)" },
      { level:"Class",   name:"Mammalia", color:"#F59E0B", desc:"Warm-blooded, feeds young with milk" },
      { level:"Order",   name:"Carnivora", color:"#10B981", desc:"Primarily meat-eating" },
      { level:"Family",  name:"Felidae",  color:"#06B6D4", desc:"The cat family" },
      { level:"Genus",   name:"Felis",    color:"#8B5CF6", desc:"Small cats" },
      { level:"Species", name:"catus",    color:"#EC4899", desc:"Domestic cat \u2014 Felis catus" },
    ];
    return (
      <div style={{background:T.card,borderRadius:14,padding:14,border:`1px solid ${T.border}`}}>
        <p style={{fontSize:11,fontWeight:800,color:T.accent,textTransform:'uppercase',letterSpacing:'.08em',margin:'0 0 4px'}}>Classification {"\u2014"} Domestic Cat</p>
        <p style={{fontSize:10,color:T.textMid,margin:'0 0 10px'}}>Tap each level to see what it means</p>
        <div style={{display:'flex',flexDirection:'column',gap:4}}>
          {tree.map((t, i) => (
            <button key={i} onClick={() => setSelected(selected===i ? null : i)}
              style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',borderRadius:9,border:`1.5px solid ${selected===i ? t.color : T.border}`,background: selected===i ? `${t.color}15` : T.surface,cursor:'pointer',fontFamily:'inherit',textAlign:'left',transition:'all .15s',marginLeft: i*8}}>
              <span style={{fontSize:9,fontWeight:700,color:t.color,background:`${t.color}20`,padding:'2px 6px',borderRadius:4,flexShrink:0,minWidth:62,textAlign:'center'}}>{t.level}</span>
              <span style={{fontSize:13,fontWeight:700,color:T.text,fontStyle:'italic'}}>{t.name}</span>
              {selected===i && <span style={{fontSize:11,color:t.color,marginLeft:'auto',fontStyle:'normal'}}>{"\u2192"}</span>}
            </button>
          ))}
        </div>
        {selected !== null && (
          <div style={{marginTop:10,padding:'10px 12px',background:`${tree[selected].color}12`,border:`1px solid ${tree[selected].color}33`,borderRadius:10}}>
            <p style={{fontSize:12,color:T.text,margin:0,lineHeight:1.5}}><strong>{tree[selected].level}:</strong> {tree[selected].desc}</p>
            <p style={{fontSize:11,color:T.textMid,margin:'4px 0 0'}}>Mnemonic: <strong>K</strong>ing <strong>P</strong>hilip <strong>C</strong>ame <strong>O</strong>ver <strong>F</strong>or <strong>G</strong>ood <strong>S</strong>oup</p>
          </div>
        )}
      </div>
    );
  }

  if (diagramType === 'energy') {
    const chains = [
      { name:'Power station', steps:[{e:'\u26AB',label:'Chemical (coal)',color:'#6B7280'},{e:'\uD83D\uDD25',label:'Thermal (heat)',color:'#EF4444'},{e:'\uD83D\uDCA8',label:'Kinetic (steam)',color:'#3B82F6'},{e:'\u26A1',label:'Electrical',color:'#F59E0B'},{e:'\uD83D\uDCA1',label:'Light & Heat',color:'#10B981'}] },
      { name:'Solar panel',   steps:[{e:'\u2600\uFE0F',label:'Light (solar)',color:'#F59E0B'},{e:'\u26A1',label:'Electrical',color:'#10B981'},{e:'\uD83D\uDCA1',label:'Light & Heat',color:'#8B5CF6'}] },
      { name:'Your body',     steps:[{e:'\uD83C\uDF4E',label:'Chemical (food)',color:'#10B981'},{e:'\uD83D\uDD25',label:'Thermal (heat)',color:'#EF4444'},{e:'\uD83C\uDFC3',label:'Kinetic (movement)',color:'#3B82F6'}] },
    ];
    const [which, setWhich] = React.useState(0);
    const chain = chains[which];
    return (
      <div style={{background:T.card,borderRadius:14,padding:14,border:`1px solid ${T.border}`}}>
        <p style={{fontSize:11,fontWeight:800,color:'#F59E0B',textTransform:'uppercase',letterSpacing:'.08em',margin:'0 0 10px'}}>Energy Transformations</p>
        <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap'}}>
          {chains.map((c,i) => (
            <button key={i} onClick={() => setWhich(i)} style={{padding:'4px 10px',borderRadius:99,border:`1.5px solid ${T.accent}`,background: which===i ? T.accent : 'transparent',color: which===i ? 'white' : T.accent,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>{c.name}</button>
          ))}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:4,overflowX:'auto',paddingBottom:4}}>
          {chain.steps.map((s, i) => (
            <React.Fragment key={i}>
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',flexShrink:0,minWidth:54}}>
                <div style={{width:44,height:44,borderRadius:'50%',background:`${s.color}20`,border:`2px solid ${s.color}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,marginBottom:4}}>{s.e}</div>
                <p style={{fontSize:9,color:s.color,fontWeight:700,textAlign:'center',margin:0,whiteSpace:'pre-line',lineHeight:1.3}}>{s.label}</p>
              </div>
              {i < chain.steps.length-1 && <div style={{fontSize:16,color:T.textDim,flexShrink:0,marginBottom:14}}>{"\u2192"}</div>}
            </React.Fragment>
          ))}
        </div>
        <p style={{fontSize:10,color:T.textMid,margin:'10px 0 0',textAlign:'center'}}>{"\u2696\uFE0F"} Law of Conservation: Energy is never created or destroyed {"\u2014"} only transformed</p>
      </div>
    );
  }

  if (diagramType === 'rocks') {
    const [sel, setSel] = React.useState(null);
    const types = [
      { id:"igneous",     icon:"\uD83C\uDF0B", label:"Igneous", color:"#EF4444", desc:"Formed from cooled magma or lava. Examples: granite (slow cooling = large crystals), basalt (fast cooling = small crystals)." },
      { id:"sedimentary", icon:"\uD83C\uDFD4\uFE0F", label:"Sedimentary", color:"#F59E0B", desc:"Formed from compressed layers of sediment over millions of years. Fossils are found here. Examples: sandstone, limestone, coal." },
      { id:"metamorphic", icon:"\uD83D\uDC8E", label:"Metamorphic", color:"#8B5CF6", desc:"Existing rock transformed by intense heat and pressure deep underground. Examples: marble (from limestone), slate (from shale)." },
    ];
    return (
      <div style={{background:T.card,borderRadius:14,padding:14,border:`1px solid ${T.border}`}}>
        <p style={{fontSize:11,fontWeight:800,color:'#92400E',textTransform:'uppercase',letterSpacing:'.08em',margin:'0 0 10px'}}>The Rock Cycle</p>
        <div style={{display:'flex',gap:8,marginBottom:12,justifyContent:'center'}}>
          {types.map(t => (
            <button key={t.id} onClick={() => setSel(sel===t.id ? null : t.id)}
              style={{flex:1,padding:'10px 6px',borderRadius:12,border:`2px solid ${sel===t.id ? t.color : T.border}`,background: sel===t.id ? `${t.color}15` : T.surface,cursor:'pointer',textAlign:'center',transition:'all .2s',fontFamily:'inherit'}}>
              <div style={{fontSize:22,marginBottom:4}}>{t.icon}</div>
              <div style={{fontSize:11,fontWeight:700,color: sel===t.id ? t.color : T.text}}>{t.label}</div>
            </button>
          ))}
        </div>
        <svg width="220" height="90" style={{display:'block',margin:'0 auto',marginBottom:8}}>
          <ellipse cx="110" cy="45" rx="90" ry="35" fill="none" stroke={T.border} strokeWidth="1.5" strokeDasharray="4,3"/>
          <text x="110" y="17" textAnchor="middle" fontSize="9" fill={T.textMid}>cooling & solidifying</text>
          <text x="12" y="55" textAnchor="start" fontSize="9" fill={T.textMid} transform="rotate(-35,12,55)">heat & pressure</text>
          <text x="195" y="55" textAnchor="end" fontSize="9" fill={T.textMid} transform="rotate(35,195,55)">erosion & layering</text>
          <circle cx="110" cy="10" r="5" fill="#EF4444"/>
          <circle cx="25" cy="65" r="5" fill="#8B5CF6"/>
          <circle cx="195" cy="65" r="5" fill="#F59E0B"/>
          <path d="M110,15 L30,62" stroke={T.border} strokeWidth="1.5" fill="none"/>
          <path d="M25,68 L100,70" stroke={T.border} strokeWidth="1.5" fill="none"/>
          <path d="M190,65 L115,15" stroke={T.border} strokeWidth="1.5" fill="none"/>
        </svg>
        {sel && (
          <div style={{padding:'10px 12px',background:`${types.find(t=>t.id===sel).color}10`,border:`1px solid ${types.find(t=>t.id===sel).color}33`,borderRadius:10}}>
            <p style={{fontSize:12,color:T.text,margin:0,lineHeight:1.6}}>{types.find(t=>t.id===sel).desc}</p>
          </div>
        )}
        {!sel && <p style={{fontSize:10,color:T.textMid,textAlign:'center',margin:0}}>Tap a rock type to learn more</p>}
      </div>
    );
  }

  if (diagramType === 'solarsystem') {
    const [tick, setTick] = React.useState(0);
    const [paused, setPaused] = React.useState(false);
    React.useEffect(() => {
      if (paused) return;
      const id = setInterval(() => setTick(t => t+1), 60);
      return () => clearInterval(id);
    }, [paused]);
    const planets = [
      { name:'Mercury', r:6,  orbit:55,  speed:4.7,  color:'#9CA3AF', emoji:'\u26AB' },
      { name:'Venus',   r:9,  orbit:80,  speed:3.5,  color:'#F59E0B', emoji:'\uD83D\uDFE1' },
      { name:'Earth',   r:10, orbit:108, speed:2.9,  color:'#3B82F6', emoji:'\uD83C\uDF0D' },
      { name:'Mars',    r:7,  orbit:138, speed:2.4,  color:'#EF4444', emoji:'\uD83D\uDD34' },
    ];
    const cx=140, cy=110;
    return (
      <div style={{background:'#0C1F1D',borderRadius:14,padding:14,border:'1px solid #1e3a38'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
          <p style={{fontSize:11,fontWeight:800,color:'#93C5FD',textTransform:'uppercase',letterSpacing:'.08em',margin:0}}>Inner Solar System</p>
          <button onClick={() => setPaused(p=>!p)} style={{padding:'3px 10px',borderRadius:99,border:'1px solid #93C5FD44',background:'transparent',color:'#93C5FD',fontSize:11,cursor:'pointer',fontFamily:'inherit'}}>{paused ? '\u25B6 Play' : '\u23F8 Pause'}</button>
        </div>
        <svg width="280" height="220" style={{display:'block',margin:'0 auto'}}>
          {[[20,15],[250,30],[40,180],[260,190],[130,5],[270,120],[10,100]].map(([x,y],i) => (
            <circle key={i} cx={x} cy={y} r="1.5" fill="white" opacity={0.4+Math.sin(tick*0.05+i)*0.3}/>
          ))}
          <circle cx={cx} cy={cy} r="20" fill="#FCD34D" opacity="0.9"/>
          <circle cx={cx} cy={cy} r="22" fill="none" stroke="#FCD34D" strokeWidth="4" opacity="0.2"/>
          <text x={cx} y={cy+5} textAnchor="middle" fontSize="16">{"\u2600\uFE0F"}</text>
          {planets.map(p => {
            const angle = (tick * p.speed * 0.018) % (2*Math.PI);
            const px = cx + p.orbit * Math.cos(angle);
            const py = cy + p.orbit * Math.sin(angle);
            return (
              <g key={p.name}>
                <ellipse cx={cx} cy={cy} rx={p.orbit} ry={p.orbit*0.35} fill="none" stroke="white" strokeWidth="0.5" opacity="0.15"/>
                <circle cx={px} cy={py} r={p.r} fill={p.color} opacity="0.9"/>
                <text x={px} y={py+3} textAnchor="middle" fontSize={p.r*1.4}>{p.emoji}</text>
                <text x={px} y={py+p.r+10} textAnchor="middle" fontSize="8" fill="white" opacity="0.7">{p.name}</text>
              </g>
            );
          })}
        </svg>
        <p style={{fontSize:10,color:'#93C5FD',textAlign:'center',margin:'4px 0 0',opacity:0.7}}>Planets closer to the Sun orbit faster {"\u2014"} Mercury takes 88 days, Earth takes 365 days</p>
      </div>
    );
  }

  if (diagramType === 'adaptations') {
    const animals = [
      { name:'Red Kangaroo', habitat:'Desert', emoji:'\uD83E\uDD98', color:'#F59E0B',
        structural:['Large back feet for efficient hopping','Light fur reflects sunlight','Large ears radiate heat'],
        behavioural:['Rests in shade during hottest hours','Most active at dawn and dusk'],
        physiological:['Concentrates urine to save water','Can slow digestion in drought'] },
      { name:'Platypus', habitat:'Freshwater', emoji:'\uD83E\uDD86', color:'#3B82F6',
        structural:['Bill detects electrical fields from prey','Waterproof fur','Webbed feet for swimming'],
        behavioural:['Hunts underwater with eyes closed (uses bill)','Burrows into riverbanks to nest'],
        physiological:['Males have venomous ankle spurs','Can lower metabolism in cold water'] },
      { name:'Thorny Devil', habitat:'Desert', emoji:'\uD83E\uDD8E', color:'#EF4444',
        structural:['Spines deter predators','Groove system channels dew to mouth','Camouflage colouring'],
        behavioural:['Moves slowly (conserves energy)','Faces wind to collect moisture'],
        physiological:['Body temperature fluctuates with environment (ectotherm)'] },
    ];
    const [sel, setSel] = React.useState(0);
    const a = animals[sel];
    return (
      <div style={{background:T.card,borderRadius:14,padding:14,border:`1px solid ${T.border}`}}>
        <p style={{fontSize:11,fontWeight:800,color:'#10B981',textTransform:'uppercase',letterSpacing:'.08em',margin:'0 0 10px'}}>Australian Animal Adaptations</p>
        <div style={{display:'flex',gap:6,marginBottom:12}}>
          {animals.map((a,i) => (
            <button key={i} onClick={() => setSel(i)} style={{flex:1,padding:'8px 4px',borderRadius:10,border:`2px solid ${sel===i ? a.color : T.border}`,background: sel===i ? `${a.color}15` : T.surface,cursor:'pointer',textAlign:'center',fontFamily:'inherit',transition:'all .2s'}}>
              <div style={{fontSize:20}}>{a.emoji}</div>
              <div style={{fontSize:9,fontWeight:700,color: sel===i ? a.color : T.textMid,marginTop:2}}>{a.name}</div>
            </button>
          ))}
        </div>
        <div style={{background:`${a.color}08`,borderRadius:12,padding:10,border:`1px solid ${a.color}22`}}>
          <p style={{fontSize:11,fontWeight:700,color:T.textMid,margin:'0 0 6px'}}>{"\uD83C\uDF0F"} Habitat: {a.habitat}</p>
          {[['\uD83E\uDDB4 Structural', a.structural],['\uD83C\uDFAD Behavioural', a.behavioural],['\u2697\uFE0F Physiological', a.physiological]].map(([label, items]) => (
            <div key={label} style={{marginBottom:6}}>
              <p style={{fontSize:10,fontWeight:700,color:a.color,margin:'0 0 3px'}}>{label}</p>
              {items.map((item,i) => <p key={i} style={{fontSize:11,color:T.text,margin:'0 0 2px',paddingLeft:8}}>{"\u2022"} {item}</p>)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (diagramType === 'cell') {
    const [sel, setSel] = React.useState(null);
    const [cellType, setCellType] = React.useState('animal');
    const parts = {
      animal: [
        {id:"membrane", label:"Cell Membrane", color:"#3B82F6", desc:"Controls what enters and exits \u2014 the cells gatekeeper"},
        {id:"cytoplasm", label:"Cytoplasm", color:"#93C5FD", desc:"Jelly-like fluid filling the cell \u2014 substances dissolve and move here"},
        {id:"nucleus", label:"Nucleus", color:"#8B5CF6", desc:"Control centre \u2014 contains DNA with all genetic instructions"},
        {id:"mito", label:"Mitochondria", color:"#EF4444", desc:"Powerhouse of the cell \u2014 converts food into energy (ATP)"},
        {id:"ribosome", label:"Ribosomes", color:"#F59E0B", desc:"Tiny structures that build proteins from amino acids"},
      ],
      plant: [
        {id:"wall", label:"Cell Wall", color:"#10B981", desc:"Rigid outer layer of cellulose \u2014 gives plants their structure"},
        {id:"membrane", label:"Cell Membrane", color:"#3B82F6", desc:"Controls what enters and exits \u2014 inside the cell wall"},
        {id:"chloro", label:"Chloroplasts", color:"#16A34A", desc:"Contain chlorophyll \u2014 capture sunlight for photosynthesis"},
        {id:"vacuole", label:"Large Vacuole", color:"#06B6D4", desc:"Stores water and maintains cell pressure (turgor)"},
        {id:"nucleus", label:"Nucleus", color:"#8B5CF6", desc:"Control centre \u2014 contains DNA"},
      ],
    };
    const current = parts[cellType];
    return (
      <div style={{background:T.card,borderRadius:14,padding:14,border:`1px solid ${T.border}`}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
          <p style={{fontSize:11,fontWeight:800,color:'#10B981',textTransform:'uppercase',letterSpacing:'.08em',margin:0}}>Cell Structure</p>
          <div style={{display:'flex',gap:4}}>
            {['animal','plant'].map(t => (
              <button key={t} onClick={() => {setCellType(t); setSel(null);}} style={{padding:'3px 10px',borderRadius:99,border:'1.5px solid #10B981',background: cellType===t ? '#10B981' : 'transparent',color: cellType===t ? 'white' : '#10B981',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit',textTransform:'capitalize'}}>{t}</button>
            ))}
          </div>
        </div>
        <svg width="220" height="150" style={{display:'block',margin:'0 auto',marginBottom:8}}>
          {cellType === 'animal' ? (
            <>
              <ellipse cx="110" cy="75" rx="95" ry="65" fill="#93C5FD22" stroke="#3B82F6" strokeWidth="2.5"/>
              <ellipse cx="100" cy="70" rx="28" ry="24" fill="#8B5CF622" stroke="#8B5CF6" strokeWidth="2" onClick={() => setSel(sel==='nucleus'?null:'nucleus')} style={{cursor:'pointer'}}/>
              <text x="100" y="74" textAnchor="middle" fontSize="9" fill="#8B5CF6" fontWeight="700">Nucleus</text>
              {[[145,50],[155,95],[70,40],[65,110],[130,120]].map(([x,y],i) => (
                <circle key={i} cx={x} cy={y} r="8" fill="#EF444422" stroke="#EF4444" strokeWidth="1.5"/>
              ))}
              <text x="148" y="72" fontSize="8" fill="#EF4444">Mito.</text>
            </>
          ) : (
            <>
              <rect x="15" y="10" width="190" height="130" rx="4" fill="#10B98122" stroke="#10B981" strokeWidth="3"/>
              <rect x="20" y="15" width="180" height="120" rx="2" fill="#93C5FD15" stroke="#3B82F6" strokeWidth="1.5"/>
              <ellipse cx="110" cy="75" rx="30" ry="22" fill="#8B5CF622" stroke="#8B5CF6" strokeWidth="2"/>
              <text x="110" y="79" textAnchor="middle" fontSize="9" fill="#8B5CF6" fontWeight="700">Nucleus</text>
              <ellipse cx="110" cy="75" rx="60" ry="40" fill="#06B6D411" stroke="#06B6D4" strokeWidth="1.5" strokeDasharray="3,2"/>
              <text x="110" y="128" textAnchor="middle" fontSize="8" fill="#06B6D4">Vacuole</text>
              {[[45,40],[170,50],[40,110],[175,105]].map(([x,y],i) => (
                <ellipse key={i} cx={x} cy={y} rx="12" ry="8" fill="#16A34A33" stroke="#16A34A" strokeWidth="1.5"/>
              ))}
              <text x="45" y="27" textAnchor="middle" fontSize="8" fill="#16A34A">Chloroplast</text>
            </>
          )}
        </svg>
        <div style={{display:'flex',flexDirection:'column',gap:4}}>
          {current.map(p => (
            <button key={p.id} onClick={() => setSel(sel===p.id?null:p.id)}
              style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',borderRadius:8,border:`1.5px solid ${sel===p.id ? p.color : T.border}`,background: sel===p.id ? `${p.color}12` : T.surface,cursor:'pointer',fontFamily:'inherit',textAlign:'left',transition:'all .15s'}}>
              <div style={{width:10,height:10,borderRadius:'50%',background:p.color,flexShrink:0}}/>
              <span style={{fontSize:12,fontWeight:600,color: sel===p.id ? p.color : T.text}}>{p.label}</span>
            </button>
          ))}
        </div>
        {sel && (
          <div style={{marginTop:8,padding:'8px 12px',background:`${current.find(p=>p.id===sel).color}10`,borderRadius:8,border:`1px solid ${current.find(p=>p.id===sel).color}33`}}>
            <p style={{fontSize:12,color:T.text,margin:0}}>{current.find(p=>p.id===sel).desc}</p>
          </div>
        )}
      </div>
    );
  }

    return null;

}
